import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model
} from "@angular/core";
import { FormsModule } from "@angular/forms";

import { NzSwitchModule } from "ng-zorro-antd/switch";

import { SwitchSizeType } from "../../types";

@Component({
  selector: "lkt-switch",
  standalone: true,
  templateUrl: "./switch.component.html",
  styleUrls: ["./switch.component.scss"],
  imports: [FormsModule, NzSwitchModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LktSwitchComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  public disabled = input<boolean>(false);
  public checked = model<boolean>(false);
  public size = input<SwitchSizeType>("medium");

  /**
   * `data-cy` do consumidor, repassado ao `<button class="ant-switch">` — o elemento que o Cypress
   * clica e que o leitor de tela anuncia. Bindar no host `<lkt-switch>` não serve para o nome
   * acessível: o leitor calcula o nome do `<button role="switch">`, não do elemento customizado.
   */
  public readonly dataCy = input<string>("");
  public readonly ariaLabel = input<string>("");

  // Map standardized sizes to ng-zorro sizes (only supports default and small)
  public readonly nzSize = computed<"small" | "default">(() =>
    this.size() === "small" ? "small" : "default"
  );

  constructor() {
    // O botão é renderizado pelo template do NzSwitchComponent, fora do alcance de binding deste
    // template. `afterRenderEffect` roda após o render e reexecuta quando os inputs mudam.
    afterRenderEffect(() => {
      const dataCy = this.dataCy();
      const ariaLabel = this.ariaLabel();
      const checked = this.checked();
      const button = this.host.nativeElement.querySelector("button.ant-switch");
      if (!button) {
        return;
      }
      this.applyAttribute(button, "data-cy", dataCy);
      this.applyAttribute(button, "aria-label", ariaLabel);
      // O nz-switch desta versão renderiza o botão SEM `role` e SEM `aria-checked` (verificado no
      // pacote instalado). Sem isto o leitor de tela anuncia um botão sem estado.
      button.setAttribute("role", "switch");
      button.setAttribute("aria-checked", checked ? "true" : "false");
    });
  }

  private applyAttribute(element: Element, attribute: string, value: string): void {
    if (value) {
      element.setAttribute(attribute, value);
      return;
    }
    element.removeAttribute(attribute);
  }
}
